import api from './api';

export interface Notification {
    id: number;
    user_id: number | null;
    title: string;
    message: string;
    type: 'info' | 'success' | 'warning' | 'error';
    read: boolean;
    inspection_id: number | null;
    created_at: string;
    read_at: string | null;
}

export interface NotificationListResponse {
    notifications: Notification[];
    total: number;
    unread_count: number;
}

export interface UnreadCountResponse {
    unread_count: number;
}

/**
 * Get all notifications with pagination
 */
export const getNotifications = async (
    skip: number = 0,
    limit: number = 20,
    unread_only: boolean = false,
    workshop_id?: number
): Promise<NotificationListResponse> => {
    const params = new URLSearchParams({
        skip: skip.toString(),
        limit: limit.toString(),
        unread_only: unread_only.toString(),
    });

    if (workshop_id !== undefined) {
        params.append('workshop_id', workshop_id.toString());
    }

    const response = await api.get<NotificationListResponse>(
        `/notifications?${params.toString()}`
    );
    return response;
};

/**
 * Get unread notification count
 */
export const getUnreadCount = async (workshop_id?: number): Promise<number> => {
    const params = new URLSearchParams();

    if (workshop_id !== undefined) {
        params.append('workshop_id', workshop_id.toString());
    }

    const queryString = params.toString();
    const url = queryString
        ? `/notifications/unread/count?${queryString}`
        : '/notifications/unread/count';

    const response = await api.get<UnreadCountResponse>(url);
    return response.unread_count;
};

/**
 * Mark specific notifications as read
 */
export const markNotificationsAsRead = async (
    notificationIds: number[]
): Promise<void> => {
    await api.put('/notifications/mark-read', {
        notification_ids: notificationIds,
    });
};

/**
 * Mark all notifications as read
 */
export const markAllAsRead = async (): Promise<void> => {
    await api.put('/notifications/mark-all-read');
};

/**
 * Delete a notification
 */
export const deleteNotification = async (id: number): Promise<void> => {
    await api.delete(`/notifications/${id}`);
};

/**
 * Create a new notification (admin only)
 */
export const createNotification = async (data: {
    title: string;
    message: string;
    type: 'info' | 'success' | 'warning' | 'error';
    user_id?: number | null;
    inspection_id?: number | null;
}): Promise<Notification> => {
    const response = await api.post<Notification>('/notifications', data);
    return response;
};
